import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { ResponsiveBar } from '@nivo/bar';
import { StateNodeConfig } from '@sierralabs/workflow';

import { parseTaskStatus } from '../tasks/task.helper';
import { DocumentStat } from '../documents/Document.interface';
import { useGetDocumentStats } from '../documents/Document.hook';
import { WorkflowEntity } from '../workflow/Workflow.interface';
import {
  useFindWorkflowsByTaskTypeTarget,
  useMultipleWorkflowStatuses
} from '../workflow/Workflow.hooks';
import { AppSnackbar } from '@sierralabs/react-material-ui';
import { TaskTypeEntity } from '../task-types/TaskType.interface';

const DocumentChart: React.FC = () => {
  const history = useHistory();
  const [stats, setStats] = useState<DocumentStat[]>([]);
  const [{ data }, get] = useGetDocumentStats();
  const { workflows, loadingError, findWorkflows } =
    useFindWorkflowsByTaskTypeTarget(TaskTypeEntity.File);
  const paths = useMultipleWorkflowStatuses(workflows);

  useEffect(() => {
    get();
  }, [get]);

  useEffect(() => {
    findWorkflows();
  }, [findWorkflows]);

  useEffect(() => {
    const allStats: DocumentStat[] = (data || []).reduce((results, item) => {
      const existingStatus = results.find(
        result => result.status === parseTaskStatus(item.status)
      );
      if (existingStatus) {
        existingStatus.value = existingStatus.value + item.value;
      } else {
        results.push({
          ...item,
          status: parseTaskStatus(item.status) || 'Unknown'
        });
      }
      return results;
    }, [] as DocumentStat[]);

    // workflow paths
    for (const path of paths) {
      // if status exists, ignore
      const status: string = parseTaskStatus(path) || 'Unknown';
      if (!allStats.find((stat: DocumentStat) => stat.status === status)) {
        allStats.push({ value: 0, status });
      }
    }

    setStats(allStats);
  }, [data, paths]);

  const getColor = (barData: DocumentStat) => {
    let state: StateNodeConfig | undefined;
    for (const workflow of workflows || []) {
      const config = workflow.config as StateNodeConfig | undefined;
      state = config?.states?.find(state => state.name === barData.status);
      if (state) {
        break;
      }
    }
    return state?.meta?.color?.backgroundColor || '#efefef';
  };

  return (
    <div style={{ height: 400 }}>
      <ResponsiveBar
        data={stats}
        keys={['value']}
        indexBy='status'
        margin={{ top: 0, right: 60, bottom: 80, left: 100 }}
        padding={0.3}
        borderRadius={5}
        layout='horizontal'
        colors={bar => getColor(bar.data)}
        onClick={data => {
          const status = data.data.status;
          history.push(`/portal/documents?status=${status}`);
        }}
        borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
        axisTop={null}
        axisRight={null}
        axisBottom={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          legend: '# of Documents',
          legendPosition: 'middle',
          legendOffset: 64
        }}
        axisLeft={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          legendPosition: 'middle',
          legendOffset: -100
        }}
        labelSkipWidth={12}
        labelSkipHeight={12}
        labelTextColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
        animate={true}
      />
      {loadingError && (
        <AppSnackbar open={Boolean(loadingError)} severity='error'>
          Error finding a published workflow for '{WorkflowEntity.File}'
        </AppSnackbar>
      )}
    </div>
  );
};

export default DocumentChart;
